import axios, {AxiosResponse} from "axios";
import {PagedResult, SpringPagedResult} from "@/service/PagedResult";
import Order from "@/model/Order";
import {OrderType, WorklogType} from "@/enums";
import {OrderAssignmentRequest, OrderEmployeeAssignment} from "@/model/OrderEmployeeAssignment";
import {OrderCustomerCategory} from "@/model/OrderCustomerCategory";
import {DownloadedFile, parseProblemBody, toFilename} from "@/service/FileDownload";

const PATH = "/v2/orders";
const PATH_V3 = "/v3/orders";
const ORDER_PAGE_SIZE = 250;
// The page size the worklogs are loaded with, the endpoint's default
const WORKLOG_PAGE_SIZE = 250;
// The order the available orders are offered in, as the removed /accounting/worklog/availableOrders returned them
const AVAILABLE_ORDER_TYPES = [OrderType.INV, OrderType.MOD, OrderType.DRY, OrderType.BEV, OrderType.MIS, OrderType.MFR];

class OrderServiceV2 {

  getOrder(orderId: number): Promise<AxiosResponse<Order>> {
    return axios.get(`${PATH}/${orderId}`);
  }

  getOrderV3(orderUuid: string): Promise<AxiosResponse<OrderV3Dto>> {
    return axios.get(`${PATH_V3}/${orderUuid}`);
  }

  /**
   * Creates an order via POST /v3/orders. Answers 201 with the created order, 404 if no customer exists for
   * `customerId` and 422 if the order is invalid.
   */
  createOrder(request: CreateOrderRequestV3): Promise<AxiosResponse<OrderV3Dto>> {
    return axios.post<OrderV3Dto>(PATH_V3, request);
  }

  /**
   * The orders active on the date that the caller may see, loading every page of GET /v3/orders. Mapped to
   * {@link Order}s addressed by the deprecated legacyId, grouped by order type (inventories first) and newest first
   * within each type.
   */
  findAvailableOrdersV3(date: string): Promise<Array<Order>> {
    return this.findOrdersActiveOnV3From(date, 0, [])
      .then(orders => orders
        .sort((a, b) => AVAILABLE_ORDER_TYPES.indexOf(a.orderType) - AVAILABLE_ORDER_TYPES.indexOf(b.orderType)
          || b.validFrom.localeCompare(a.validFrom))
        .map(order => Object.assign(new Order(), {
          id: order.legacyId,
          uuid: order.id,
          name: order.name,
          type: order.orderType,
          validFrom: order.validFrom,
          validTo: order.validTo ?? null
        })));
  }

  private findOrdersActiveOnV3From(date: string, page: number, orders: Array<OrderV3Dto>): Promise<Array<OrderV3Dto>> {
    return axios.get<SpringPagedResult<OrderV3Dto>>(PATH_V3, {
      params: {
        activeOn: date,
        page: page,
        size: ORDER_PAGE_SIZE
      }
    }).then(response => {
      const loaded = orders.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findOrdersActiveOnV3From(date, page + 1, loaded)
        : loaded;
    });
  }

  /**
   * Applies a JSON Merge Patch to the order's master data via PATCH /v3/orders/{orderUuid}. Fields left out keep their
   * value, a field set to null is cleared. Answers 200 with the patched order and 422 if the patched order is invalid.
   */
  updateOrder(orderUuid: string, patch: unknown): Promise<AxiosResponse<unknown>> {
    return axios.patch(`${PATH_V3}/${orderUuid}`, patch, {headers: {"Content-Type": "application/merge-patch+json"}});
  }

  /**
   * Deletes an order via DELETE /v3/orders/{orderUuid}. Answers 204 on success and 409 if worklogs, per diems or cash
   * flows are recorded on the order.
   */
  deleteOrder(orderUuid: string): Promise<AxiosResponse<void>> {
    return axios.delete<void>(`${PATH_V3}/${orderUuid}`);
  }

  findOrdersInTimeframe(from: string, to: string, include: string | null = null, orderType: OrderType | null = null): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH}`, {
      params: {
        from: from,
        to: to,
        include: include,
        orderType: orderType,
        size: 1000
      }
    });
  }

  /**
   * Exports the orders the caller may see whose validity period overlaps the period from `activeFrom` to `activeTo`
   * (`YYYY-MM-DD`, both included) as a CSV file, via GET /v3/orders with `Accept: text/csv`. Without `orderTypes`,
   * the orders of every type. Answers 403 if the caller may not list orders and 422 if `activeTo` lies before
   * `activeFrom`.
   */
  exportOrdersV3(activeFrom: string, activeTo: string, orderTypes: Array<OrderType> = []): Promise<DownloadedFile> {
    return axios.get(PATH_V3, {
      params: {
        activeFrom: activeFrom,
        activeTo: activeTo,
        orderTypes: orderTypes
      },
      // Repeats orderTypes=INV&orderTypes=MOD, as Spring binds it, instead of orderTypes[]=…
      paramsSerializer: {indexes: null},
      headers: {Accept: "text/csv"},
      responseType: "arraybuffer",
      transformResponse: parseProblemBody
    })
      .then(response => ({
        filename: toFilename(response.headers["content-disposition"], "orders.csv"),
        content: new Blob([response.data], {type: "text/csv;charset=utf-8"})
      }));
  }

  // Worklogs, addressed by the order's uuid
  /**
   * The worklogs of an order via GET /v3/orders/{orderUuid}/worklogs, ordered by day, start time and employee name,
   * loading every page of the paged endpoint. Without `month` (`YYYY-MM`), the worklogs of every month; without
   * `worklogTypes`, those of every type.
   */
  findOrderWorklogsV3(orderUuid: string, month: string | null = null, worklogTypes: Array<WorklogType> = []): Promise<Array<OrderWorklogV3Dto>> {
    return this.findOrderWorklogsV3From(orderUuid, month, worklogTypes, 0, []);
  }

  private findOrderWorklogsV3From(orderUuid: string, month: string | null, worklogTypes: Array<WorklogType>, page: number,
                                  worklogs: Array<OrderWorklogV3Dto>): Promise<Array<OrderWorklogV3Dto>> {
    return axios.get<SpringPagedResult<OrderWorklogV3Dto>>(`${PATH_V3}/${orderUuid}/worklogs`, {
      params: {
        month: month ?? undefined,
        worklogTypes: worklogTypes,
        page: page,
        size: WORKLOG_PAGE_SIZE
      },
      // Repeats worklogTypes=WORK&worklogTypes=TRAVEL, as Spring binds it, instead of worklogTypes[]=…
      paramsSerializer: {indexes: null}
    }).then(response => {
      const loaded = worklogs.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findOrderWorklogsV3From(orderUuid, month, worklogTypes, page + 1, loaded)
        : loaded;
    });
  }

  // Assignments, addressed by the order's and the employee's uuid
  getOrderAssignments(orderUuid: string): Promise<AxiosResponse<{ assignments: Array<OrderEmployeeAssignment> }>> {
    return axios.get(`${PATH}/${orderUuid}/assignments`);
  }

  updateOrderAssignment(orderUuid: string, employeeUuid: string, request: OrderAssignmentRequest): Promise<AxiosResponse<OrderEmployeeAssignment>> {
    return axios.put(`${PATH}/${orderUuid}/assignments/${employeeUuid}`, request);
  }

  deleteOrderAssignment(orderUuid: string, employeeUuid: string) {
    return axios.delete(`${PATH}/${orderUuid}/assignments/${employeeUuid}`);
  }

  // Checklist of an inventory, addressed by the order's uuid
  getOrderChecklist(orderUuid: string): Promise<AxiosResponse<OrderChecklistResponse>> {
    return axios.get(`${PATH}/${orderUuid}/checklist`);
  }

  /**
   * Completes the item now, by the caller. Completing an item that is already completed keeps when and by whom it was
   * completed.
   */
  completeChecklistItem(orderUuid: string, code: string): Promise<AxiosResponse<ChecklistItemDto>> {
    return axios.post(`${PATH}/${orderUuid}/checklist/${code}/complete`);
  }

  // Per diems, addressed by the order's and the employees' uuids
  /**
   * The days of a month on which employees logged time on the order, with their per diems. Without `month`, the
   * month the order's per diems were most recently recorded in; the response's `month` tells which one.
   */
  getOrderPerDiems(orderUuid: string, month: string | null = null): Promise<AxiosResponse<OrderPerDiemsResponse>> {
    return axios.get(`${PATH}/${orderUuid}/per-diems`, {
      params: month ? {month: month} : {}
    });
  }

  /**
   * Gives several employees the same per diems on one day, recorded for the order. Both amounts 0 without breakfast
   * deletes their per diems on that day instead. Applied to all employees or, if any of them is rejected, to none.
   */
  updateOrderPerDiems(orderUuid: string, request: OrderPerDiemsBulkRequest): Promise<AxiosResponse<void>> {
    return axios.post(`${PATH}/${orderUuid}/per-diems/_bulk`, request);
  }
}

/**
 * An order as the v3 api (GET /v3/orders/{orderUuid}) returns it. `id` is the order's uuid.
 */
export interface OrderV3Dto {
  id: string;
  /** @deprecated The order's numeric id, only provided for backward compatibility */
  legacyId?: number;
  name: string;
  orderType: OrderType;
  validFrom: string;
  validTo?: string;
}

/**
 * The body of POST /v3/orders. `customerId` is the customer's uuid. A project (INV, MOD, MIS, MFR) needs a `validTo`.
 * `sendMail` tells the customer of an inventory by mail that it was created and is ignored for any other order type.
 */
export interface CreateOrderRequestV3 {
  name: string;
  orderType: OrderType;
  validFrom: string;
  validTo: string | null;
  startTime: string | null;
  address: string | null;
  zip: string | null;
  city: string;
  positions: number | null;
  notes: string | null;
  customerId: string | null;
  customerCategory: OrderCustomerCategory;
  sendMail: boolean;
}

/**
 * The request creating the order. Its team leader, cost center and checklist cannot be set on creation.
 */
export const toCreateOrderRequestV3 = (order: Order, sendMail: boolean): CreateOrderRequestV3 => ({
  name: order.name as string,
  orderType: order.orderType as OrderType,
  validFrom: order.validFrom,
  validTo: order.validTo,
  startTime: order.startTime,
  address: order.address,
  zip: order.zip,
  city: order.city as string,
  positions: order.positions ?? null,
  notes: order.notes ?? null,
  customerId: order.customer?.uuid ?? null,
  customerCategory: order.customerCategory,
  sendMail: sendMail
});

/**
 * One worklog of GET /v3/orders/{orderId}/worklogs. `id` and the employee's `id` are uuids, `start` and `end` are times
 * of day in the form `HH:mm` and `minutes` is the time between them. `createdBy` and `createdAt` tell who recorded the
 * worklog and when; clocking out or changing a worklog replaces it, so they belong to its latest change.
 */
export interface OrderWorklogV3Dto {
  id: string;
  employee: {
    id: string;
    employeeNumber: number;
    firstName: string;
    lastName: string;
  };
  date: string;
  start: string;
  end: string;
  minutes: number;
  type: WorklogType;
  comment: string | null;
  createdBy: string;
  createdAt: string;
}

/**
 * An item of the checklist of an inventory. `label` is in the language `Accept-Language` asks for.
 */
export interface ChecklistItemDto {
  code: string;
  phase: "BEFORE" | "DURING" | "AFTER";
  label: string;
  completed?: boolean;
  completedAt?: string;
  completedBy?: string;
}

/**
 * The checklist of an inventory, its items in the order they are worked through.
 */
export interface OrderChecklistResponse {
  items: Array<ChecklistItemDto>;
}

/**
 * The per diem of an employee on one day as the v2 order API returns it. Its `id` is a uuid and the order is
 * referenced by its uuid and name only; it may be recorded for another order than the one asked for.
 */
export interface OrderPerDiemDto {
  id: string;
  order: { id: string; name: string };
  catering: number;
  misc: number;
  breakfast?: boolean;
  cateringAfterBreakfast: number;
}

/**
 * An employee who logged time on the order on one day, with the minutes per worklog type and their per diem of that
 * day, if any.
 */
export interface OrderPerDiemEmployeeDayDto {
  employee: { id: string; employeeNumber?: number; firstName: string; lastName: string };
  workMinutes?: number;
  travelMinutes?: number;
  passengerMinutes?: number;
  perDiem?: OrderPerDiemDto;
}

export interface OrderPerDiemDayDto {
  date: string;
  editable?: boolean;
  employees: Array<OrderPerDiemEmployeeDayDto>;
}

/**
 * The per diems of an order in one month. `days` only lists days on which time was logged, oldest first.
 */
export interface OrderPerDiemsResponse {
  month: string;
  days: Array<OrderPerDiemDayDto>;
}

/**
 * The per diems to give several employees of an order on one day.
 */
export interface OrderPerDiemsBulkRequest {
  date: string;
  employeeIds: Array<string>;
  catering: number;
  misc: number;
  breakfast: boolean;
}

export default new OrderServiceV2();
